"use client";

import { useCallback, useEffect, useState } from "react";

const COOKIE_NAME = "zipvibe_beta_dismissed";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year

type ShouldShowBetaBannerArgs = {
  hasDismissed: boolean;
  isPublicE2ETestMode: boolean;
};

export function shouldShowBetaBanner(args: ShouldShowBetaBannerArgs): boolean {
  return !args.hasDismissed && !args.isPublicE2ETestMode;
}

function hasDismissedCookie(): boolean {
  if (typeof document === "undefined") return false;
  return document.cookie.split("; ").some((c) => c.startsWith(`${COOKIE_NAME}=1`));
}

function setDismissedCookie(): void {
  const secure =
    typeof window !== "undefined" && window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${COOKIE_NAME}=1; Path=/; Max-Age=${COOKIE_MAX_AGE}; SameSite=Lax${secure}`;
}

export function BetaBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (
      shouldShowBetaBanner({
        hasDismissed: hasDismissedCookie(),
        isPublicE2ETestMode: process.env.NEXT_PUBLIC_E2E_TEST_MODE === "1",
      })
    ) {
      setVisible(true);
    }
  }, []);

  const dismiss = useCallback(() => {
    setDismissedCookie();
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-[#1f2b27]/35 px-4 backdrop-blur-sm"
      aria-live="polite"
    >
      <aside className="relative w-full max-w-lg overflow-hidden rounded-2xl border border-[#d8cab2] bg-[#fffaf1] shadow-[0_18px_36px_rgba(20,31,27,0.2)]">
        {/* animated construction stripes */}
        <div
          className="pointer-events-none absolute inset-0 animate-beta-stripe-scroll opacity-[0.04]"
          aria-hidden="true"
          style={{
            backgroundImage:
              "repeating-linear-gradient(120deg, #047857 0px, #047857 10px, transparent 10px, transparent 20px)",
            backgroundSize: "28px 100%",
          }}
        />

        <div className="relative flex flex-col items-center gap-4 p-6 text-center sm:p-8">
          {/* pulsing dot */}
          <span className="relative flex h-3 w-3" aria-hidden="true">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#059669] opacity-60" />
            <span className="relative inline-flex h-3 w-3 rounded-full bg-[#10b981]" />
          </span>

          <h2 className="font-[family-name:var(--font-outfit)] text-lg font-semibold text-[#047857] sm:text-xl">
            Early Access
          </h2>

          <p className="text-sm leading-relaxed text-[#32463f]">
            This platform is under active development. Features, content, and pricing may change at
            any time. By continuing, you acknowledge that the service is provided with limited
            functionality and may be subject to interruptions.
          </p>

          <button
            type="button"
            onClick={dismiss}
            className="mt-2 inline-flex min-h-10 items-center justify-center rounded-lg border border-[#0f766e]/30 bg-[#0f766e] px-5 text-sm font-semibold text-white transition-colors hover:bg-[#0d6962]"
          >
            I understand, continue
          </button>
        </div>
      </aside>
    </div>
  );
}
